import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useIdleDetector } from '../hooks/useIdleDetector';
import { useAuth } from '../hooks/useAuth';
import { useSip } from '../hooks/useSip';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { Screensaver, type ScreensaverPhoto } from './Screensaver';
import type { ScreensaverMode } from '../hooks/useDisplaySettings';
import { DEFAULT_BACKGROUND_URL } from '../constants/defaults';

const SCREENSAVER_TIMEOUT = 180_000; // 3 minutes
const REFRESH_INTERVAL = 15 * 60 * 1000; // Refresh photo list every 15 minutes

// Shown in the screensaver when the resident has no photos of their own, so the
// screensaver displays the stock background (with the clock overlay) instead of
// a black clock-only screen.
const DEFAULT_SCREENSAVER_PHOTO: ScreensaverPhoto = {
  id: 0,
  photoUuid: 'default',
  url: DEFAULT_BACKGROUND_URL,
  thumbnailUrl: null,
  description: null,
  uploaderName: null,
  reaction: 'neutral',
  takenAt: null,
  viewCount: 0,
  createdAt: '1970-01-01T00:00:00.000Z',
};

/**
 * ScreensaverController — manages idle detection, photo fetching, and screensaver lifecycle.
 * Renders a photo screensaver when idle for 3 minutes and photos are available.
 * Falls back to clock-only mode when photos mode is selected but no photos exist.
 */
export function ScreensaverController() {
  const { api, isAuthenticated } = useAuth();
  const { currentCall } = useSip();
  const { settings, setSleep } = useDisplaySettings();
  const hasActiveCall = currentCall !== null;

  // Disable idle detection during active calls, in sleep mode, or when the
  // screensaver is disabled for this location (portal display default).
  const { isIdle, wake } = useIdleDetector(
    SCREENSAVER_TIMEOUT,
    !hasActiveCall && !settings.sleepMode && settings.screensaverEnabled,
  );

  const [photos, setPhotos] = useState<ScreensaverPhoto[]>([]);
  const [nextEvent, setNextEvent] = useState<{ title: string; startsAt: string; locationName?: string } | null>(null);
  const refreshRef = useRef<ReturnType<typeof setInterval>>();

  // Fetch screensaver photos
  const fetchPhotos = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await api.get<{ success: boolean; data: ScreensaverPhoto[] }>('/api/v1/user/photos/screensaver');
      console.log('[Screensaver] Raw API response type:', typeof result, 'keys:', result ? Object.keys(result) : 'null');

      // Handle both wrapped { success, data } and direct array responses
      let photoList: ScreensaverPhoto[] = [];
      if (result?.success && Array.isArray(result.data)) {
        photoList = result.data;
      } else if (Array.isArray(result)) {
        // In case api.get() or a middleware unwraps the envelope
        photoList = result as unknown as ScreensaverPhoto[];
      } else if (result?.data && Array.isArray(result.data)) {
        photoList = result.data;
      } else {
        console.warn('[Screensaver] Unexpected response format:', JSON.stringify(result)?.substring(0, 200));
      }

      console.log('[Screensaver] Fetched', photoList.length, 'photos');
      if (photoList.length > 0) {
        console.log('[Screensaver] First photo URL:', photoList[0].url?.substring(0, 120));
        console.log('[Screensaver] First photo keys:', Object.keys(photoList[0]));
      }
      setPhotos(photoList);
    } catch (err) {
      console.warn('[Screensaver] Failed to fetch photos:', err);
    }
  }, [api, isAuthenticated]);

  // Fetch next upcoming event (for display on screensaver)
  const fetchNextEvent = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await api.get<{ success: boolean; data: any[] }>('/api/v1/user/agenda/today');
      if (result?.success && result.data) {
        const now = new Date();
        const upcoming = result.data.find((e: any) => new Date(e.startsAt) > now);
        if (upcoming) {
          setNextEvent({
            title: upcoming.title,
            startsAt: upcoming.startsAt,
            locationName: upcoming.locationName
          });
        } else {
          setNextEvent(null);
        }
      }
    } catch {
      // Agenda endpoint may not exist yet — fail silently
    }
  }, [api, isAuthenticated]);

  // Initial fetch + periodic refresh
  useEffect(() => {
    fetchPhotos();
    fetchNextEvent();

    refreshRef.current = setInterval(() => {
      fetchPhotos();
      fetchNextEvent();
    }, REFRESH_INTERVAL);

    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [fetchPhotos, fetchNextEvent]);

  // Handle reaction
  const handleReaction = useCallback(async (photoId: number, reaction: 'liked' | 'disliked') => {
    try {
      await api.post(`/api/v1/user/photos/${photoId}/reaction`, { reaction });
      // Update local state optimistically
      setPhotos((prev) =>
        prev
          .map((p) => (p.id === photoId ? { ...p, reaction } : p))
          .filter((p) => p.reaction !== 'disliked') // Remove disliked from screensaver
      );
    } catch (err) {
      console.warn('[Screensaver] Failed to submit reaction:', err);
    }
  }, [api]);

  // Wake up if a call comes in
  useEffect(() => {
    if (hasActiveCall) {
      if (isIdle) wake();
      if (settings.sleepMode) setSleep(false);
    }
  }, [hasActiveCall, isIdle, settings.sleepMode, wake, setSleep]);

  const screensaverMode = settings.screensaverMode as ScreensaverMode;

  // Log state for debugging
  useEffect(() => {
    console.log('[ScreensaverController] state:', {
      isIdle,
      hasActiveCall,
      screensaverMode,
      photosCount: photos.length,
      sleepMode: settings.sleepMode,
      firstPhotoUrl: photos[0]?.url?.substring(0, 60),
    });
  }, [isIdle, photos.length, screensaverMode, hasActiveCall, settings.sleepMode, photos]);

  // Don't render if not idle, call active, or screensaver disabled for this
  // location (portal display default).
  if (!isIdle || hasActiveCall || !settings.screensaverEnabled) {
    return null;
  }

  // In photos mode with no resident photos, show the stock default background
  // (with clock overlay) instead of falling back to a black clock-only screen.
  const effectivePhotos: ScreensaverPhoto[] =
    photos.length > 0
      ? photos
      : (screensaverMode === 'photos' ? [DEFAULT_SCREENSAVER_PHOTO] : []);

  const effectiveMode: ScreensaverMode =
    (screensaverMode === 'photos' && effectivePhotos.length === 0)
      ? 'clock-only'
      : screensaverMode;

  console.log('[ScreensaverController] rendering Screensaver, mode:', effectiveMode, 'photos:', effectivePhotos.length);

  return (
    <Screensaver
      photos={effectivePhotos}
      onWake={wake}
      onReaction={handleReaction}
      nextEvent={nextEvent}
      mode={effectiveMode}
    />
  );
}
